import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PROVIDED_JOB_POST_LISTS_QUERY_KEY } from './useProvidedJobPostLists';
import compact from '@/utils/compact';
import type { SearchQuery } from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';

/** 個別IDを指定するモード */
type ManualParams = {
  listIds: number[];
  providedJobPostIds: number[];
};

/** フィルタ条件 + 除外IDを指定するモード（全件選択時） */
type FilterParams = {
  listIds: number[];
  searchQuery: SearchQuery;
  excludedProvidedJobPostIds: number[];
};

type BulkAddParams = ManualParams | FilterParams;

const isFilterParams = (p: BulkAddParams): p is FilterParams =>
  (p as FilterParams).searchQuery !== undefined;

export default function useBulkAddToLists() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: BulkAddParams) => {
      const csrfToken =
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const body = isFilterParams(params)
        ? {
            list_ids: params.listIds,
            search_query: compact(params.searchQuery),
            excluded_provided_job_post_ids: params.excludedProvidedJobPostIds,
          }
        : {
            list_ids: params.listIds,
            provided_job_post_ids: params.providedJobPostIds,
          };
      const response = await fetch('/agent/api/provided_job_post_lists/bulk_add.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorMessage = 'Failed to add to lists';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDED_JOB_POST_LISTS_QUERY_KEY] });
    },
  });
}
